/**
 * The main screen: the creature, the day's totals, and what has been logged.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError, type Day, type Entry } from "../lib/api";
import { eat, momentOf, moodFor, poopAroundLine, sip, type Moment } from "../lib/creature";
import { pickUp, poopsOnTheGround, poopsSoFar } from "../lib/poops";
import { Creature, PoopPile } from "../components/Creature";
import { Totals } from "../components/Totals";
import { clockTime, describe, shift, today } from "../lib/day";

const icons: Record<string, string> = { food: "🍽️", drink: "🥤", exercise: "🏃" };

export function Today() {
  const [date, setDate] = useState(today());
  const [day, setDay] = useState<Day | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moment, setMoment] = useState<Moment | null>(null);
  const [now, setNow] = useState(() => new Date());
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async (which: string) => {
    setError(null);
    try {
      setDay(await api.day(which));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : String(problem));
    }
  }, []);

  useEffect(() => {
    void load(date);
  }, [date, load]);

  // Just logged something? The logging form says so when it sends her here.
  // It waits for the day to load, so the creature is on screen to enjoy it, and
  // then clears the message so reloading the page doesn't feed it again.
  const logged: Entry | undefined = location.state?.logged;
  useEffect(() => {
    if (!logged || !day) return;
    if (logged.kind === "food") setMoment(eat(logged.name));
    if (logged.kind === "drink") setMoment(sip(logged.name));
    window.scrollTo(0, 0);
    navigate(location.pathname, { replace: true, state: null });
  }, [logged, day, location.pathname, navigate]);

  // Moments end by themselves.
  useEffect(() => {
    if (!moment) return;
    const timer = setTimeout(() => setMoment(null), moment.ms);
    return () => clearTimeout(timer);
  }, [moment]);

  // Keep an eye on the clock, so a poop can arrive while she is looking.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  // A poop arriving while she watches gets a little wiggle. Ones that came
  // while the app was closed are just there when she gets back.
  const isToday = date === today();
  const poopCount = isToday ? poopsSoFar(date, now).length : 0;
  const lastPoopCount = useRef(poopCount);
  useEffect(() => {
    if (poopCount > lastPoopCount.current) {
      setMoment((current) => current ?? momentOf("pooping"));
    }
    lastPoopCount.current = poopCount;
  }, [poopCount]);

  async function remove(id: number) {
    // Deleting is the one thing here that cannot be undone, so it asks first.
    if (!confirm("Remove this from the day?")) return;
    await api.deleteEntry(id);
    void load(date);
  }

  if (error) {
    return (
      <>
        <h1 className="screen-title">Today</h1>
        <div className="notice notice--bad">{error}</div>
      </>
    );
  }

  if (!day) {
    return (
      <div className="empty">
        <div className="spinner" style={{ margin: "0 auto 10px" }} />
        Loading…
      </div>
    );
  }

  const feeling = moodFor(day, new Date().getHours());
  const poops = isToday ? poopsOnTheGround(date, now) : [];
  const line = moment?.line ?? (poops.length > 0 ? poopAroundLine(date) : feeling.line);

  return (
    <>
      <div className="day-nav">
        <button onClick={() => setDate(shift(date, -1))} aria-label="The day before">
          ‹
        </button>
        <h1 className="screen-title" style={{ margin: 0 }}>
          {describe(date)}
        </h1>
        <button
          onClick={() => setDate(shift(date, 1))}
          disabled={isToday}
          aria-label="The day after"
        >
          ›
        </button>
      </div>

      <div className="creature-stage">
        <div className="creature-yard">
          <Creature mood={feeling.mood} name={day.settings.creature_name} moment={moment} />
          {poops.map((poop) => (
            <PoopPile
              key={poop.id}
              poop={poop}
              onPick={() => setMoment(momentOf("thanks"))}
              onPicked={() => {
                pickUp(date, poop.id);
                setNow(new Date());
              }}
            />
          ))}
        </div>
        <div className="creature-name">{day.settings.creature_name}</div>
        <p className="creature-says">{line}</p>
      </div>

      <Totals day={day} />

      <div className="card">
        <div className="spread" style={{ marginBottom: 6 }}>
          <h2 style={{ fontSize: "1rem" }}>Logged</h2>
          <Link to="/add" className="small" style={{ color: "var(--accent)" }}>
            + Add
          </Link>
        </div>

        {day.entries.length === 0 ? (
          <div className="empty">
            Nothing logged yet.
            <div style={{ marginTop: 14 }}>
              <Link to="/add" className="button">
                Log something
              </Link>
            </div>
          </div>
        ) : (
          day.entries.map((entry) => (
            <div className="entry" key={entry.id}>
              <div className="entry__icon">{icons[entry.kind] ?? "🍽️"}</div>
              <div className="entry__main">
                <div className="entry__name">{entry.name}</div>
                <div className="muted small">
                  {clockTime(entry.logged_at)}
                  {entry.kind === "exercise"
                    ? entry.minutes
                      ? ` · ${Math.round(entry.minutes)} min`
                      : ""
                    : entry.calories != null
                      ? ` · ${Math.round(entry.calories)} kcal`
                      : ""}
                  {entry.kind === "exercise" && entry.calories
                    ? ` · −${Math.round(entry.calories)} kcal`
                    : ""}
                </div>
              </div>
              <button
                className="entry__delete"
                onClick={() => void remove(entry.id)}
                aria-label={`Remove ${entry.name}`}
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}
